//@ts-nocheck

import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Button,
  TextField,
  Checkbox,
  ListItemText,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/system";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import api from "../../utils/Url";
import CustomLabel from "../../CustomLable";

/* -------------------- Styles -------------------- */
const containerStyle = {
  width: "100%",
  height: "500px",
};

const StyledRadioGroup = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: "10px",
  backgroundColor: "#f4f6f8",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
}));

/* -------------------- Component -------------------- */
export default function Tracking() {
  const { t } = useTranslation();

  const [vehicleTypes, setVehicleTypes] = useState<any[]>([]);
  const [vehicleNos, setVehicleNos] = useState<any[]>([]);
  const [vType, setVType] = useState<number[]>([]);
  const [vNo, setVNo] = useState<number[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [activeVehicle, setActiveVehicle] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  const [center] = useState({
    lat: 26.447965,
    lng: 80.3432,
  });

  const mapRef = useRef<any>(null);

  /* -------------------- Initial Load -------------------- */
  useEffect(() => {
    fetchVehicleTypes();
    fetchVehicleNos();
    fetchTrackingData(); // show ALL vehicles on load
  }, []);

  /* -------------------- APIs -------------------- */

  const fetchVehicleTypes = async () => {
    const res = await api.get("Rep/GetVehicleType");

    setVehicleTypes(
      res.data.data.map((x: any) => ({
        label: x.vehicleType || "N/A",
        value: x.itemTypeid,
      }))
    );
  };

  const fetchVehicleNos = async () => {
    const res = await api.get("Rep/GetVehiclesm");

    setVehicleNos(
      res.data.data.map((x: any) => ({
        label: x.vehicleNo,
        value: x.itemMasterid,
      }))
    );
  };

  /* -------- PROCESS ALL LOCATIONS FROM API -------- */
  const processAllLocations = (apiData: any[]) => {
    console.log("API Data received:", apiData);

    if (!Array.isArray(apiData)) {
      console.error("Data is not an array:", apiData);
      return [];
    }

    console.log(`Total API records: ${apiData.length}`);

    // Process ALL records - no filtering
    const allLocations = apiData.map((record: any, index: number) => {
      // Extract data from your exact API response structure
      const lat = parseFloat(record.lattitude || 0);  // Note: two 't's in "lattitude"
      const lng = parseFloat(record.longitude || 0);
      const vehicleNo = record.vehicleNo || record.itemName || `Vehicle_${index}`;
      const vehicleType = record.vehicleType || "N/A";
      const speed = parseFloat(record.speed || record.speedDecimal || 0);
      const driver = record.driver || "NA";
      const department = record.department || "NA";
      const trackTime = record.trackTime || new Date().toISOString();
      const deviceId = record.deviceId || "NA";
      const itemMasterId = record.itemmasterid || 0;
      const isRunning = record.acc === true; // Using acc field for running/stopped status

      // Format the track time properly
      const formattedTime = new Date(trackTime).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });

      return {
        id: `${vehicleNo}_${index}_${Date.now()}_${Math.random()}`,
        vehicleNo: vehicleNo,
        vehicleTypename: vehicleType,
        lat: lat,
        lng: lng,
        speed: speed,
        empName: driver,
        departmentname: department,
        tracktime: formattedTime,
        deviceId: deviceId,
        itemMasterId: itemMasterId,
        isRunning: isRunning, // Add isRunning field based on acc
        originalIndex: index + 1, // 1-based index
        originalData: record,
        icon: isRunning
          ? "https://png.pngtree.com/png-vector/20240128/ourmid/pngtree-green-truck-transport-png-image_11508688.png"
          : "https://png.pngtree.com/png-vector/20240128/ourmid/pngtree-red-truck-transportation-png-image_11507611.png",
      };
    });

    // Filter only valid coordinates
    const validLocations = allLocations.filter(v => {
      const isValid = v.lat !== 0 && v.lng !== 0 && !isNaN(v.lat) && !isNaN(v.lng);
      return isValid;
    });

    console.log(`Valid locations: ${validLocations.length} out of ${apiData.length}`);
    console.log(`Running vehicles: ${validLocations.filter(v => v.isRunning).length}`);
    console.log(`Stopped vehicles: ${validLocations.filter(v => !v.isRunning).length}`);

    return validLocations;
  };

  const fetchTrackingData = async () => {
    setLoading(true);

    // Build payload according to API requirements
    const payload: any = {
      forDate: selectedDate
    };

    // Only include parameters if they have values
    if (vNo.length > 0) {
      payload.itemMasterids = vNo;
    }

    if (vType.length > 0) {
      payload.itemTypeids = vType;
    }

    console.log("API Payload:", payload);

    try {
      const res = await api.post("Mng/DaySelected", payload);

      console.log("Full API Response:", res.data);

      // Extract data array from your API response structure
      let apiData = [];

      if (res.data.isSuccess && Array.isArray(res.data.data)) {
        apiData = res.data.data;
      } else {
        console.warn("API did not return success or data array:", res.data);
      }

      console.log("Total records from API:", apiData.length);

      if (apiData.length === 0) {
        console.warn("No location data received from API");
        setVehicles([]);
        return;
      }

      // Process ALL locations
      const allLocations = processAllLocations(apiData);
      console.log("All processed locations:", allLocations);

      setVehicles(allLocations);

      // Adjust map bounds if we have locations
      if (allLocations.length > 0 && mapRef.current) {
        setTimeout(() => adjustMapBounds(allLocations), 100);
      } else if (allLocations.length === 0) {
        // Reset to default center if no locations
        if (mapRef.current) {
          mapRef.current.setCenter(center);
          mapRef.current.setZoom(12);
        }
      }

    } catch (error) {
      console.error("Error fetching tracking data:", error);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- Map Helpers -------------------- */
  const adjustMapBounds = (vehicleList: any[]) => {
    if (!vehicleList.length || !mapRef.current) return;

    try {
      const bounds = new window.google.maps.LatLngBounds();
      vehicleList.forEach((v) => {
        if (v.lat && v.lng) {
          bounds.extend(new window.google.maps.LatLng(v.lat, v.lng));
        }
      });

      // Only adjust bounds if we have valid points
      if (!bounds.isEmpty()) {
        mapRef.current.fitBounds(bounds, 50); // 50px padding

        // If all points are at same location, zoom in more
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const latDiff = Math.abs(ne.lat() - sw.lat());
        const lngDiff = Math.abs(ne.lng() - sw.lng());

        if (latDiff < 0.001 && lngDiff < 0.001) {
          mapRef.current.setZoom(15);
        }
      }

    } catch (error) {
      console.error("Error adjusting map bounds:", error);
    }
  };

  /* -------------------- Handle Map Load -------------------- */
  const handleMapLoad = (map: any) => {
    mapRef.current = map;
    setMapReady(true);

    // If we already have vehicles, adjust the map bounds
    if (vehicles.length > 0) {
      setTimeout(() => adjustMapBounds(vehicles), 100);
    }
  };

  /* -------------------- Handle Date Change -------------------- */
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  /* -------------------- Reset Filters -------------------- */
  const resetFilters = () => {
    setVType([]);
    setVNo([]);
    setSelectedDate(new Date().toISOString().split("T")[0]);
    fetchTrackingData(); // Fetch with reset filters
  };

  /* -------------------- UI -------------------- */
  return (
    <Grid container spacing={2}>
      {/* Date Selector */}
      {/* <Grid item md={2}>
        <StyledRadioGroup>
          <TextField
            type="date"
            size="small"
            fullWidth
            label={<CustomLabel text={t("text.SelectDate")} />}
            value={selectedDate}
            onChange={handleDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            disabled={loading}
          />
        </StyledRadioGroup>
      </Grid> */}

      {/* Vehicle Type */}
      <Grid item md={5}>
        <StyledRadioGroup>
          <Autocomplete
            multiple
            options={vehicleTypes}
            disableCloseOnSelect
            onChange={(e, v) => setVType(v.map((x: any) => x.value))}
            disabled={loading}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} />
                <ListItemText primary={option.label} />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={<CustomLabel text={t("text.SelectVehicleType")} />}
              />
            )}
          />
        </StyledRadioGroup>
      </Grid>

      {/* Vehicle No */}
      <Grid item md={5}>
        <StyledRadioGroup>
          <Autocomplete
            multiple
            options={vehicleNos}
            disableCloseOnSelect
            onChange={(e, v) => setVNo(v.map((x: any) => x.value))}
            disabled={loading}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} />
                <ListItemText primary={option.label} />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={<CustomLabel text={t("text.SelectVehicleNo")} />}
              />
            )}
          />
        </StyledRadioGroup>
      </Grid>

      {/* Action Buttons */}
      <Grid item md={2}>
        <StyledRadioGroup style={{ display: "flex", gap: "8px" }}>
          <Button
            fullWidth
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={18} /> : <VisibilityIcon />
            }
            style={{
              backgroundColor: "var(--header-background)",
              color: "#fff",
              flex: 2,
            }}
            onClick={fetchTrackingData}
          >
            {loading ? "Loading..." : "Show"}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            style={{
              flex: 1,
              minWidth: "auto",
            }}
            onClick={resetFilters}
            disabled={loading}
            title="Reset Filters"
          >
            Reset
          </Button>
        </StyledRadioGroup>
      </Grid>

      {/* Map */}
      <Grid item xs={12}>
        <div style={{ position: "relative", height: "500px" }}>
          {!mapReady && (
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              zIndex: 1,
            }}>
              <CircularProgress />
            </div>
          )}

          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={12}
            onLoad={handleMapLoad}
            options={{
              streetViewControl: true,
              mapTypeControl: true,
              fullscreenControl: true,
              zoomControl: true,
            }}
          >
            {vehicles.map((v) => (
              <Marker
                key={v.id}
                position={{ lat: v.lat, lng: v.lng }}
                icon={{
                  url: v.icon,
                  scaledSize: new window.google.maps.Size(50, 50),
                }}
                onMouseOver={() => setActiveVehicle(v)}
                onClick={() => setActiveVehicle(v)}
              >
                {activeVehicle?.id === v.id && (
                  <InfoWindow
                    position={{ lat: v.lat, lng: v.lng }}
                    onCloseClick={() => setActiveVehicle(null)}
                  >
                    <div style={{ minWidth: 250, padding: "12px" }}>
                      <div style={{
                        fontWeight: "bold",
                        marginBottom: "10px",
                        color: "#1976d2",
                        fontSize: "1.1em",
                        borderBottom: "2px solid #f0f0f0",
                        paddingBottom: "8px"
                      }}>
                        {v.vehicleNo}
                      </div>
                      <div style={{ fontSize: "0.9em", lineHeight: "1.6" }}>
                        <div><strong>📍 Vehicle Type:</strong> {v.vehicleTypename}</div>
                        <div><strong>🚦 Status:</strong>
                          <span style={{
                            color: v.isRunning ? "#4caf50" : "#f44336",
                            fontWeight: "bold",
                            marginLeft: "4px"
                          }}>
                            {v.isRunning ? "RUNNING" : "STOPPED"}
                          </span>
                        </div>
                        <div><strong>📏 Speed:</strong> {v.speed} km/h</div>
                        <div><strong>👨‍✈️ Driver:</strong> {v.empName}</div>
                        <div><strong>🏢 Department:</strong> {v.departmentname}</div>
                        <div><strong>📱 Device ID:</strong> {v.deviceId}</div>
                        <div><strong>🕐 Track Time:</strong> {v.tracktime}</div>
                        <div style={{
                          fontSize: "0.8em",
                          color: "#666",
                          marginTop: "12px",
                          paddingTop: "8px",
                          borderTop: "1px solid #e0e0e0",
                          fontFamily: "monospace"
                        }}>
                          Coordinates: {v.lat?.toFixed(6)}, {v.lng?.toFixed(6)}
                        </div>
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            ))}
          </GoogleMap>

          {/* Summary Card */}
          {vehicles.length > 0 && mapReady && (
            <div style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              backgroundColor: "white",
              padding: "16px",
              borderRadius: "8px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.25)",
              zIndex: 10,
              minWidth: "260px",
              border: "1px solid #e0e0e0",
            }}>
              <div style={{
                fontWeight: "bold",
                marginBottom: "12px",
                fontSize: "1em",
                color: "#1a237e",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <span style={{
                  backgroundColor: "#1976d2",
                  color: "white",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.9em"
                }}>
                  📍
                </span>
                Location Summary
              </div>
              <div style={{ fontSize: "0.9em", lineHeight: "1.8" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Total Points:</span>
                  <strong style={{ color: "#1976d2", fontSize: "1.1em" }}>{vehicles.length}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Unique Vehicles:</span>
                  <strong>{new Set(vehicles.map(v => v.vehicleNo)).size}</strong>
                </div>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "8px",
                  paddingTop: "8px",
                  borderTop: "1px solid #f0f0f0"
                }}>
                  <span style={{ color: "#4caf50" }}>
                    ✅ Running:
                  </span>
                  <strong style={{ color: "#4caf50" }}>
                    {vehicles.filter(v => v.isRunning).length}
                  </strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#f44336" }}>
                    ⛔ Stopped:
                  </span>
                  <strong style={{ color: "#f44336" }}>
                    {vehicles.filter(v => !v.isRunning).length}
                  </strong>
                </div>
                <div style={{
                  fontSize: "0.8em",
                  color: "#666",
                  marginTop: "12px",
                  paddingTop: "8px",
                  borderTop: "1px solid #e0e0e0"
                }}>
                  <div><strong>Date:</strong> {selectedDate}</div>
                  <div style={{ marginTop: "4px", fontStyle: "italic" }}>
                    Showing ALL location records
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No Data Message */}
          {!loading && vehicles.length === 0 && mapReady && (
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(255,255,255,0.95)",
              padding: "24px 32px",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              zIndex: 10,
              textAlign: "center",
              border: "1px solid #e0e0e0",
              minWidth: "300px",
            }}>
              <div style={{
                fontSize: "48px",
                marginBottom: "16px",
                color: "#9e9e9e"
              }}>
                📍
              </div>
              <div style={{
                fontWeight: "bold",
                marginBottom: "12px",
                color: "#616161",
                fontSize: "1.2em"
              }}>
                No Location Data Found
              </div>
              <div style={{
                fontSize: "0.95em",
                color: "#9e9e9e",
                lineHeight: "1.5",
                marginBottom: "20px"
              }}>
                The API returned no location data for the selected filters.
              </div>
              <div style={{
                fontSize: "0.85em",
                color: "#757575",
                backgroundColor: "#f5f5f5",
                padding: "8px 12px",
                borderRadius: "6px",
                fontFamily: "monospace"
              }}>
                Selected Date: {selectedDate}
              </div>
            </div>
          )}
        </div>
      </Grid>
    </Grid>
  );
}

















// import React, { useEffect, useRef, useState } from "react";
// import {
//   Grid,
//   Button,
//   TextField,
//   Checkbox,
//   ListItemText,
//   CircularProgress,
// } from "@mui/material";
// import { Autocomplete } from "@mui/material";
// import { styled } from "@mui/system";
// import VisibilityIcon from "@mui/icons-material/Visibility";
// import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
// import { useTranslation } from "react-i18next";
// import api from "../../utils/Url";
// import CustomLabel from "../../CustomLable";

// /* -------------------- Styles -------------------- */
// const containerStyle = {
//   width: "100%",
//   height: "500px",
// };

// const StyledRadioGroup = styled("div")(({ theme }) => ({
//   padding: theme.spacing(2),
//   borderRadius: "10px",
//   backgroundColor: "#f4f6f8",
//   boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
// }));

// /* -------------------- Component -------------------- */
// export default function Tracking() {
//   const { t } = useTranslation();

//   const [vehicleTypes, setVehicleTypes] = useState<any[]>([]);
//   const [vehicleNos, setVehicleNos] = useState<any[]>([]);
//   const [vType, setVType] = useState<number[]>([]);
//   const [vNo, setVNo] = useState<number[]>([]);
//   const [vehicles, setVehicles] = useState<any[]>([]);
//   const [activeVehicle, setActiveVehicle] = useState<any>(null);
//   const [loading, setLoading] = useState(false);

//   const [center, setCenter] = useState({
//     lat: 26.447965,
//     lng: 80.3432,
//   });

//   const mapRef = useRef<any>(null);

//   /* -------------------- Initial Load -------------------- */
//   useEffect(() => {
//     fetchVehicleTypes();
//     fetchVehicleNos();
//     fetchTrackingData(); // show ALL vehicles on load
//   }, []);

//   /* -------------------- APIs -------------------- */

//   const fetchVehicleTypes = async () => {
//     const res = await api.get("Rep/GetVehicleType");

//     setVehicleTypes(
//       res.data.data.map((x: any) => ({
//         label: x.vehicleType || "NA",
//         value: x.itemTypeid,
//       }))
//     );
//   };

//   const fetchVehicleNos = async () => {
//     const res = await api.get("Rep/GetVehiclesm");

//     setVehicleNos(
//       res.data.data.map((x: any) => ({
//         label: x.vehicleNo,
//         value: x.itemMasterid,
//       }))
//     );
//   };

//   /* -------- CORE FIX: last location per vehicle -------- */
//   const getLastLocations = (data: any[]) => {
//     const map = new Map();

//     data.forEach((x) => {
//       const key = x.itemMasterid;

//       if (!map.has(key)) {
//         map.set(key, x);
//       } else {
//         const prev = map.get(key);
//         if (new Date(x.trackTime) > new Date(prev.trackTime)) {
//           map.set(key, x);
//         }
//       }
//     });

//     return Array.from(map.values());
//   };

//   const fetchTrackingData = async () => {
//     setLoading(true);

//     const payload = {
//       itemMasterids: vNo.length ? vNo : [null],
//       itemTypeids: vType.length ? vType : [null],
//       forDate: null,
//     };

//     const res = await api.post("Rep/GetVehicleTrack", payload);

//     const lastPoints = getLastLocations(res.data.data || []);

//     const mapped = lastPoints.map((x: any, index: number) => ({
//       id: index,
//       vehicleNo: x.vehicleNo || "NA",
//       vehicleTypename: x.vehicleType || "NA",
//       lat: x.latitude,
//       lng: x.longitude,
//       speed: x.speed ?? 0,
//       empName: x.driver || "NA",
//       departmentname: x.dept || "NA",
//       tracktime: new Date(x.trackTime).toLocaleString(),
//       icon:
//         x.speed === 0
//           ? "https://png.pngtree.com/png-vector/20240128/ourmid/pngtree-red-truck-transportation-png-image_11507611.png"
//           : "https://png.pngtree.com/png-vector/20240128/ourmid/pngtree-green-truck-transport-png-image_11508688.png",
//     }));

//     setVehicles(mapped);
//     adjustMap(mapped);
//     setLoading(false);
//   };

//   /* -------------------- Map Helpers -------------------- */
//   const adjustMap = (list: any[]) => {
//     if (!list.length || !mapRef.current) return;

//     const bounds = new window.google.maps.LatLngBounds();
//     list.forEach((v) =>
//       bounds.extend(new window.google.maps.LatLng(v.lat, v.lng))
//     );

//     mapRef.current.fitBounds(bounds);

//     const avgLat = list.reduce((s, v) => s + v.lat, 0) / list.length;
//     const avgLng = list.reduce((s, v) => s + v.lng, 0) / list.length;

//     setCenter({ lat: avgLat, lng: avgLng });
//   };

//   /* -------------------- UI -------------------- */
//   return (
//     <Grid container spacing={2}>
//       {/* Vehicle Type */}
//       <Grid item md={5}>
//         <StyledRadioGroup>
//           <Autocomplete
//             multiple
//             options={vehicleTypes}
//             disableCloseOnSelect
//             onChange={(e, v) => setVType(v.map((x: any) => x.value))}
//             renderOption={(props, option, { selected }) => (
//               <li {...props}>
//                 <Checkbox checked={selected} />
//                 <ListItemText primary={option.label} />
//               </li>
//             )}
//             renderInput={(params) => (
//               <TextField
//                 {...params}
//                 size="small"
//                 label={<CustomLabel text={t("text.SelectVehicleType")} />}
//               />
//             )}
//           />
//         </StyledRadioGroup>
//       </Grid>

//       {/* Vehicle No */}
//       <Grid item md={5}>
//         <StyledRadioGroup>
//           <Autocomplete
//             multiple
//             options={vehicleNos}
//             disableCloseOnSelect
//             onChange={(e, v) => setVNo(v.map((x: any) => x.value))}
//             renderOption={(props, option, { selected }) => (
//               <li {...props}>
//                 <Checkbox checked={selected} />
//                 <ListItemText primary={option.label} />
//               </li>
//             )}
//             renderInput={(params) => (
//               <TextField
//                 {...params}
//                 size="small"
//                 label={<CustomLabel text={t("text.SelectVehicleNo")} />}
//               />
//             )}
//           />
//         </StyledRadioGroup>
//       </Grid>

//       {/* Show Button */}
//       <Grid item md={2}>
//         <StyledRadioGroup>
//           <Button
//             fullWidth
//             disabled={loading}
//             startIcon={
//               loading ? <CircularProgress size={18} /> : <VisibilityIcon />
//             }
//             style={{
//               backgroundColor: "var(--header-background)",
//               color: "#fff",
//             }}
//             onClick={fetchTrackingData}
//           >
//             {loading ? "Loading..." : "Show"}
//           </Button>
//         </StyledRadioGroup>
//       </Grid>

//       {/* Map */}
//       <Grid item xs={12}>
//         <GoogleMap
//           mapContainerStyle={containerStyle}
//           center={center}
//           zoom={12}
//           onLoad={(map) => (mapRef.current = map)}
//         >
//           {vehicles.map((v) => (
//             <Marker
//               key={v.id}
//               position={{ lat: v.lat, lng: v.lng }}
//               icon={{
//                 url: v.icon,
//                 scaledSize: new window.google.maps.Size(44, 44),
//               }}
//               onMouseOver={() => setActiveVehicle(v)}
//               onMouseOut={() => setActiveVehicle(null)}
//             >
//               {activeVehicle?.id === v.id && (
//                 <InfoWindow>
//                   <div style={{ minWidth: 220 }}>
//                     <b>Vehicle:</b> {v.vehicleNo} <br />
//                     <b>Type:</b> {v.vehicleTypename} <br />
//                     <b>Speed:</b> {v.speed} <br />
//                     <b>Department:</b> {v.departmentname} <br />
//                     <b>Driver:</b> {v.empName} <br />
//                     <b>Date & Time:</b> {v.tracktime}
//                   </div>
//                 </InfoWindow>
//               )}
//             </Marker>
//           ))}
//         </GoogleMap>
//       </Grid>
//     </Grid>
//   );
// }

